import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { ENDPOINTS } from "@/api/application-api";
import { useOptionalAnalyticsQuery } from "@/components/RequestsAnalytics/analytics-query-context";
import StructuredSearchbar, { useStructuredSearchStore } from "@/components/StructuredSearch";
import { isSelectedEndTimeBeforeNow } from "@/components/StructuredSearch/utils/isSelectedEndTimeBeforeNow";
import { useAgGridTableRefresh } from "@/components/ui/ag-grid-table/ag-grid-table-refresh-context";
import { queryKeys } from "@/constants";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useNewSpans } from "@/queries";
import { displayErrorToast } from "@/utils/toast";
import { isWorkspaceTracesAnalyticsPathname } from "@/utils/workspacePathMatchers";

const TracesSearchbar = observer(() => {
  const structuredSearchStore = useStructuredSearchStore();
  const tableRefresh = useAgGridTableRefresh();
  const analyticsQuery = useOptionalAnalyticsQuery();
  const queryClient = useQueryClient();
  const userToken = useUserToken();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const { pathname } = useLocation();
  const { data: newSpansData } = useNewSpans();
  const hasNewData = (newSpansData?.count_spans ?? 0) > 0;
  const isTraceAnalyticsRoute = isWorkspaceTracesAnalyticsPathname(pathname);
  const analyticsFetchingCount = useIsFetching({
    queryKey: [queryKeys.traces, "analytics-custom-charts"],
    exact: false,
  });

  const handleManualRefresh = useCallback(() => {
    try {
      if (isTraceAnalyticsRoute) {
        queryClient.setQueriesData([ENDPOINTS.new_spans, userToken, workspaceId], {
          count_spans: 0,
        });
        const now = new Date();
        const shouldAdvanceEnd = isSelectedEndTimeBeforeNow(
          structuredSearchStore.dateRange.to,
          now,
        );
        if (shouldAdvanceEnd) {
          structuredSearchStore.setDateRange({
            from: structuredSearchStore.dateRange.from,
            to: now,
          });
        }
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.traces, "analytics-custom-charts"],
          exact: false,
        });
        return;
      }

      tableRefresh.triggerRefresh();
    } catch {
      displayErrorToast("Error refreshing traces");
    }
  }, [
    isTraceAnalyticsRoute,
    queryClient,
    structuredSearchStore,
    tableRefresh,
    userToken,
    workspaceId,
  ]);

  const isRefreshing = isTraceAnalyticsRoute
    ? analyticsFetchingCount > 0 || Boolean(analyticsQuery?.isSearchEndpointRunning)
    : tableRefresh.isTableLoading;

  return (
    <div className="pb-2 pt-4">
      <StructuredSearchbar
        className="w-full"
        showHeader={false}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        hasNewData={hasNewData}
      />
    </div>
  );
});

export default TracesSearchbar;
